// Procedural HTML5 Canvas "feel preview" for generated game blueprints.
// Not a real SpriteKit runtime — renders a small, playable sketch of the
// gameplay loop using the blueprint's genre, mechanics, and scoring.
//
// Three behavior templates are picked from blueprint.genre:
//   • runner   — side-scroller: jump (Space/Tap) over obstacles, collect coins
//   • puzzle   — 6x5 grid: click two adjacent same-color tiles to clear
//   • shooter  — top-down: arrow keys to move, Space to fire

const THEME = {
  cream: "#FFF8EC",
  paper: "#FBF1DC",
  ink: "#1F1A17",
  ink2: "#6B5E53",
  marigold: "#E0761F",
  saffron: "#F4A73A",
  teal: "#2A7D7B",
  border: "#E6D9BF",
};

function classifyGenre(genre = "") {
  const g = String(genre).toLowerCase();
  if (/match|tile|block|puzzle|sudoku|bejewel|candy/.test(g)) return "puzzle";
  if (/shoot|arcade|shmup|invader|defense|tower|bullet/.test(g)) return "shooter";
  // runner / platformer / endless / card / rpg / default
  return "runner";
}

function esc(s = "") {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function buildPreviewHtml(bp, spriteUrls = []) {
  if (!bp) return "<p>No blueprint</p>";
  const mode = classifyGenre(bp.genre);
  const title = esc(bp.title || "Your Game");
  const genre = esc(bp.genre || "Game");
  const engine = esc(bp.engine || "SpriteKit");
  const scoringLabel = esc((bp.scoring && bp.scoring.primary) || "Score");
  const mechanicHint = esc(
    (bp.player_mechanics && bp.player_mechanics[0] && bp.player_mechanics[0].name) ||
      (mode === "puzzle"
        ? "Click two adjacent same-color tiles"
        : mode === "shooter"
        ? "Arrow keys to move · Space to fire"
        : "Tap / Space to jump")
  );
  const concept = esc(bp.concept || "");
  const theme = esc((bp.level_design && bp.level_design[0] && bp.level_design[0].theme) || "");
  const playerSprite = spriteUrls[0] || null;
  const enemySprite = spriteUrls[1] || null;
  const coinSprite = spriteUrls[2] || null;

  const dataJson = JSON.stringify({
    title,
    genre,
    engine,
    scoringLabel,
    mode,
    playerSprite,
    enemySprite,
    coinSprite,
    theme,
  });

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${title} — Browser Preview</title>
<style>
  :root {
    --cream: ${THEME.cream};
    --paper: ${THEME.paper};
    --ink: ${THEME.ink};
    --ink2: ${THEME.ink2};
    --marigold: ${THEME.marigold};
    --saffron: ${THEME.saffron};
    --teal: ${THEME.teal};
    --border: ${THEME.border};
  }
  * { box-sizing: border-box; }
  html, body { height: 100%; margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background: var(--cream); color: var(--ink); }
  body { display: flex; flex-direction: column; align-items: center; padding: 16px; gap: 10px; }
  .hud { width: 100%; max-width: 640px; display: flex; justify-content: space-between; align-items: baseline; font-size: 13px; }
  .hud .title { font-weight: 700; }
  .hud .meta { color: var(--ink2); }
  .stage {
    position: relative;
    width: 100%;
    max-width: 640px;
    aspect-ratio: 16 / 10;
    border: 2px solid var(--ink);
    border-radius: 14px;
    background: var(--paper);
    overflow: hidden;
    box-shadow: 0 6px 0 var(--ink);
  }
  canvas { display: block; width: 100%; height: 100%; touch-action: none; }
  .overlay {
    position: absolute; inset: 0; display: flex; flex-direction: column;
    align-items: center; justify-content: center; text-align: center;
    background: rgba(31, 26, 23, 0.72); color: var(--cream); padding: 18px;
    gap: 10px; opacity: 0; pointer-events: none; transition: opacity .25s;
  }
  .overlay.show { opacity: 1; pointer-events: auto; }
  .overlay h2 { margin: 0; font-size: 20px; font-weight: 700; }
  .overlay p { margin: 0; font-size: 13px; max-width: 420px; line-height: 1.5; opacity: .9; }
  .btn {
    background: var(--marigold); color: var(--cream); border: none; border-radius: 999px;
    padding: 10px 18px; font-weight: 700; cursor: pointer; box-shadow: 0 3px 0 var(--ink);
    font-size: 13px;
  }
  .btn:active { transform: translateY(1px); box-shadow: 0 2px 0 var(--ink); }
  .hint { font-size: 12px; color: var(--ink2); max-width: 640px; text-align: center; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; background: rgba(224,118,31,.15); color: var(--marigold); font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
  .score { font-variant-numeric: tabular-nums; font-weight: 700; color: var(--marigold); }
  .banner { font-size: 11px; color: var(--ink2); padding: 4px 10px; border: 1px dashed var(--border); border-radius: 999px; }
</style>
</head>
<body>
  <div class="hud">
    <div class="title">${title} <span class="badge">${engine}</span> <span class="badge" style="background:rgba(42,125,123,.15);color:var(--teal)">${genre}</span></div>
    <div class="meta"><span id="scoreLabel">${scoringLabel}</span>: <span class="score" id="score">0</span></div>
  </div>
  <div class="stage">
    <canvas id="stage" width="960" height="600"></canvas>
    <div class="overlay show" id="startOverlay">
      <h2>Preview: ${title}</h2>
      <p>${concept || "A feel-preview of your game idea. Not the final SpriteKit build — download the starter zip for the real thing."}</p>
      <p style="opacity:.7"><strong>${mechanicHint}</strong></p>
      <button class="btn" id="startBtn" data-testid="preview-start-button">Start</button>
    </div>
    <div class="overlay" id="gameOverOverlay">
      <h2>Round complete</h2>
      <p>Final ${scoringLabel.toLowerCase()}: <span id="finalScore" class="score">0</span></p>
      <button class="btn" id="restartBtn" data-testid="preview-restart-button">Play again</button>
    </div>
  </div>
  <div class="hint">${mechanicHint}${theme ? ` · theme: ${theme}` : ""}</div>

<script>
  const DATA = ${dataJson};
  const COLORS = ${JSON.stringify(THEME)};
  const cv = document.getElementById('stage');
  const ctx = cv.getContext('2d');
  const startOverlay = document.getElementById('startOverlay');
  const gameOverOverlay = document.getElementById('gameOverOverlay');
  const scoreEl = document.getElementById('score');
  const finalScoreEl = document.getElementById('finalScore');

  function loadImg(url) {
    return new Promise((resolve) => {
      if (!url) return resolve(null);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  let playerImg = null, enemyImg = null, coinImg = null;
  let running = false;
  let score = 0;
  let rafId = null;
  let keys = {};
  let touches = { jump: false };

  window.addEventListener('keydown', (e) => {
    keys[e.key] = true;
    if ([' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) e.preventDefault();
    if (e.key === ' ') touches.jump = true;
  }, { passive: false });
  window.addEventListener('keyup', (e) => { keys[e.key] = false; if (e.key === ' ') touches.jump = false; });
  cv.addEventListener('pointerdown', () => { touches.jump = true; });
  cv.addEventListener('pointerup', () => { touches.jump = false; });

  function setScore(n) { score = n; scoreEl.textContent = String(Math.floor(n)); }

  // ========== RUNNER ==========
  function runRunner() {
    const W = cv.width, H = cv.height;
    const GROUND = H - 80;
    const player = { x: 120, y: GROUND - 48, w: 48, h: 48, vy: 0, onGround: true };
    const GRAVITY = 1.4;
    const JUMP = -22;
    let obstacles = [];
    let coins = [];
    let ticker = 0;
    let speed = 6;
    let alive = true;

    function reset() {
      player.y = GROUND - player.h; player.vy = 0; player.onGround = true;
      obstacles = []; coins = []; ticker = 0; speed = 6; alive = true; setScore(0);
    }
    reset();

    function drawBg() {
      // Sky gradient
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#FFEFC7'); g.addColorStop(1, COLORS.paper);
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      // Ground strip
      ctx.fillStyle = COLORS.ink; ctx.fillRect(0, GROUND + 44, W, 4);
      ctx.fillStyle = 'rgba(31,26,23,.08)';
      for (let i = 0; i < 16; i++) {
        const x = ((ticker * 2) % 80) * -1 + i * 80;
        ctx.fillRect(x, GROUND + 48, 40, 6);
      }
      // Sun
      ctx.fillStyle = COLORS.saffron;
      ctx.beginPath(); ctx.arc(W - 120, 110, 44, 0, Math.PI * 2); ctx.fill();
    }

    function drawPlayer() {
      if (playerImg) {
        ctx.drawImage(playerImg, player.x, player.y, player.w, player.h);
      } else {
        ctx.fillStyle = COLORS.marigold;
        roundRect(player.x, player.y, player.w, player.h, 10);
        ctx.fillStyle = COLORS.cream;
        ctx.fillRect(player.x + 12, player.y + 14, 8, 8);
        ctx.fillRect(player.x + 28, player.y + 14, 8, 8);
      }
    }

    function drawObstacle(o) {
      if (enemyImg) {
        ctx.drawImage(enemyImg, o.x, o.y, o.w, o.h);
      } else {
        ctx.fillStyle = COLORS.ink;
        roundRect(o.x, o.y, o.w, o.h, 6);
      }
    }

    function drawCoin(c) {
      if (coinImg) {
        ctx.drawImage(coinImg, c.x - 16, c.y - 16, 32, 32);
      } else {
        ctx.fillStyle = COLORS.saffron;
        ctx.beginPath(); ctx.arc(c.x, c.y, 14, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = COLORS.marigold;
        ctx.beginPath(); ctx.arc(c.x, c.y, 8, 0, Math.PI * 2); ctx.fill();
      }
    }

    function roundRect(x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath(); ctx.fill();
    }

    function step() {
      if (!running) return;
      ticker++;
      // Jump
      if ((keys[' '] || keys['ArrowUp'] || touches.jump) && player.onGround) {
        player.vy = JUMP; player.onGround = false;
      }
      player.vy += GRAVITY;
      player.y += player.vy;
      if (player.y >= GROUND - player.h) { player.y = GROUND - player.h; player.vy = 0; player.onGround = true; }

      // Spawn
      if (ticker % 80 === 0) {
        const h = 30 + Math.random() * 40;
        obstacles.push({ x: W + 20, y: GROUND - h, w: 26 + Math.random() * 20, h });
      }
      if (ticker % 55 === 0) {
        coins.push({ x: W + 20, y: GROUND - 80 - Math.random() * 100 });
      }
      obstacles.forEach(o => o.x -= speed);
      coins.forEach(c => c.x -= speed);
      obstacles = obstacles.filter(o => o.x > -60);
      coins = coins.filter(c => c.x > -30);

      // Collisions
      for (const o of obstacles) {
        if (player.x < o.x + o.w && player.x + player.w > o.x && player.y < o.y + o.h && player.y + player.h > o.y) {
          alive = false;
        }
      }
      for (let i = coins.length - 1; i >= 0; i--) {
        const c = coins[i];
        if (Math.abs(c.x - (player.x + player.w / 2)) < 24 && Math.abs(c.y - (player.y + player.h / 2)) < 24) {
          coins.splice(i, 1); setScore(score + 10);
        }
      }

      setScore(score + 0.1 * (speed / 6));
      speed = Math.min(14, 6 + ticker / 800);

      // Draw
      drawBg();
      for (const c of coins) drawCoin(c);
      for (const o of obstacles) drawObstacle(o);
      drawPlayer();

      if (!alive) return endRound();
      rafId = requestAnimationFrame(step);
    }

    function endRound() {
      running = false;
      finalScoreEl.textContent = String(Math.floor(score));
      gameOverOverlay.classList.add('show');
    }

    return { start: () => { running = true; reset(); rafId = requestAnimationFrame(step); }, stop: () => { running = false; cancelAnimationFrame(rafId); } };
  }

  // ========== PUZZLE ==========
  function runPuzzle() {
    const W = cv.width, H = cv.height;
    const COLS = 7, ROWS = 5;
    const cell = Math.min(Math.floor((W - 60) / COLS), Math.floor((H - 60) / ROWS));
    const gx = Math.floor((W - cell * COLS) / 2);
    const gy = Math.floor((H - cell * ROWS) / 2);
    const palette = [COLORS.marigold, COLORS.saffron, COLORS.teal, COLORS.ink, '#A4C2A5', '#D58CA0'];
    let grid = [];
    let selected = null;

    function refill() {
      grid = [];
      for (let r = 0; r < ROWS; r++) {
        const row = [];
        for (let c = 0; c < COLS; c++) row.push(Math.floor(Math.random() * palette.length));
        grid.push(row);
      }
    }

    function draw() {
      ctx.fillStyle = COLORS.paper; ctx.fillRect(0, 0, W, H);
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const v = grid[r][c];
          if (v == null) continue;
          const x = gx + c * cell + 6;
          const y = gy + r * cell + 6;
          ctx.fillStyle = palette[v];
          const w = cell - 12, h = cell - 12;
          ctx.beginPath();
          const rad = 14;
          ctx.moveTo(x + rad, y);
          ctx.arcTo(x + w, y, x + w, y + h, rad);
          ctx.arcTo(x + w, y + h, x, y + h, rad);
          ctx.arcTo(x, y + h, x, y, rad);
          ctx.arcTo(x, y, x + w, y, rad);
          ctx.closePath(); ctx.fill();
          if (selected && selected[0] === r && selected[1] === c) {
            ctx.strokeStyle = COLORS.cream; ctx.lineWidth = 4; ctx.stroke();
          }
        }
      }
    }

    function findMatches() {
      const hits = [];
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS - 2; c++) {
          const v = grid[r][c];
          if (v != null && v === grid[r][c+1] && v === grid[r][c+2]) hits.push([r,c],[r,c+1],[r,c+2]);
        }
      }
      for (let c = 0; c < COLS; c++) {
        for (let r = 0; r < ROWS - 2; r++) {
          const v = grid[r][c];
          if (v != null && v === grid[r+1][c] && v === grid[r+2][c]) hits.push([r,c],[r+1,c],[r+2,c]);
        }
      }
      return hits;
    }

    function collapse(hits) {
      if (!hits.length) return 0;
      const set = new Set(hits.map(([r,c]) => r + ',' + c));
      set.forEach(key => { const [r,c] = key.split(',').map(Number); grid[r][c] = null; });
      // gravity
      for (let c = 0; c < COLS; c++) {
        const col = [];
        for (let r = 0; r < ROWS; r++) if (grid[r][c] != null) col.push(grid[r][c]);
        while (col.length < ROWS) col.unshift(Math.floor(Math.random() * palette.length));
        for (let r = 0; r < ROWS; r++) grid[r][c] = col[r];
      }
      return set.size;
    }

    cv.addEventListener('click', (ev) => {
      if (!running) return;
      const rect = cv.getBoundingClientRect();
      const scale = cv.width / rect.width;
      const x = (ev.clientX - rect.left) * scale;
      const y = (ev.clientY - rect.top) * scale;
      if (x < gx || y < gy || x > gx + cell * COLS || y > gy + cell * ROWS) return;
      const c = Math.floor((x - gx) / cell);
      const r = Math.floor((y - gy) / cell);
      if (!selected) { selected = [r,c]; draw(); return; }
      const [r0,c0] = selected;
      if (Math.abs(r0-r) + Math.abs(c0-c) === 1) {
        const tmp = grid[r0][c0]; grid[r0][c0] = grid[r][c]; grid[r][c] = tmp;
        let cleared = 0; let hits = findMatches();
        while (hits.length) { cleared += collapse(hits); hits = findMatches(); }
        if (cleared === 0) {
          const tmp2 = grid[r0][c0]; grid[r0][c0] = grid[r][c]; grid[r][c] = tmp2;
        } else {
          setScore(score + cleared * 15);
        }
      }
      selected = null; draw();
    });

    function loop() {
      if (!running) return;
      draw();
      rafId = requestAnimationFrame(loop);
    }

    return {
      start: () => { refill(); setScore(0); running = true; rafId = requestAnimationFrame(loop); },
      stop: () => { running = false; cancelAnimationFrame(rafId); },
    };
  }

  // ========== SHOOTER ==========
  function runShooter() {
    const W = cv.width, H = cv.height;
    const player = { x: W / 2, y: H - 80, w: 46, h: 46, cooldown: 0 };
    let bullets = [];
    let enemies = [];
    let ticker = 0;
    let alive = true;

    function reset() { bullets = []; enemies = []; ticker = 0; alive = true; setScore(0); player.x = W / 2; }
    reset();

    function step() {
      if (!running) return;
      ticker++;
      // Player move
      if (keys['ArrowLeft']) player.x -= 8;
      if (keys['ArrowRight']) player.x += 8;
      player.x = Math.max(24, Math.min(W - 24, player.x));
      // Fire
      if ((keys[' '] || touches.jump) && player.cooldown <= 0) {
        bullets.push({ x: player.x, y: player.y - 20, vy: -12 });
        player.cooldown = 12;
      }
      player.cooldown = Math.max(0, player.cooldown - 1);
      // Spawn enemies
      if (ticker % 45 === 0) {
        enemies.push({ x: 40 + Math.random() * (W - 80), y: -30, vy: 2 + Math.random() * 1.6, w: 40, h: 40 });
      }
      enemies.forEach(e => e.y += e.vy);
      bullets.forEach(b => b.y += b.vy);
      enemies = enemies.filter(e => e.y < H + 40);
      bullets = bullets.filter(b => b.y > -20);
      // Collisions: bullet→enemy
      for (let i = enemies.length - 1; i >= 0; i--) {
        const e = enemies[i];
        let hit = false;
        for (let j = bullets.length - 1; j >= 0; j--) {
          const b = bullets[j];
          if (Math.abs(b.x - (e.x + e.w/2)) < 22 && Math.abs(b.y - (e.y + e.h/2)) < 22) {
            bullets.splice(j,1); hit = true; break;
          }
        }
        if (hit) { enemies.splice(i,1); setScore(score + 25); }
      }
      // enemy→player
      for (const e of enemies) {
        if (Math.abs(e.x + e.w/2 - player.x) < 30 && Math.abs(e.y + e.h/2 - player.y) < 30) alive = false;
      }

      // Draw
      ctx.fillStyle = '#0F1318'; ctx.fillRect(0, 0, W, H);
      // Stars
      ctx.fillStyle = 'rgba(255,255,255,.35)';
      for (let i = 0; i < 40; i++) {
        const sx = (i * 97 + ticker) % W;
        const sy = (i * 53 + ticker * 2) % H;
        ctx.fillRect(sx, sy, 2, 2);
      }
      // Bullets
      ctx.fillStyle = COLORS.saffron;
      for (const b of bullets) ctx.fillRect(b.x - 2, b.y, 4, 12);
      // Enemies
      for (const e of enemies) {
        if (enemyImg) ctx.drawImage(enemyImg, e.x, e.y, e.w, e.h);
        else { ctx.fillStyle = COLORS.marigold; ctx.fillRect(e.x, e.y, e.w, e.h); }
      }
      // Player
      if (playerImg) ctx.drawImage(playerImg, player.x - player.w/2, player.y - player.h/2, player.w, player.h);
      else {
        ctx.fillStyle = COLORS.teal;
        ctx.beginPath();
        ctx.moveTo(player.x, player.y - 24);
        ctx.lineTo(player.x - 20, player.y + 20);
        ctx.lineTo(player.x + 20, player.y + 20);
        ctx.closePath(); ctx.fill();
      }

      if (!alive) {
        running = false;
        finalScoreEl.textContent = String(Math.floor(score));
        gameOverOverlay.classList.add('show');
        return;
      }
      rafId = requestAnimationFrame(step);
    }

    return { start: () => { reset(); running = true; rafId = requestAnimationFrame(step); }, stop: () => { running = false; cancelAnimationFrame(rafId); } };
  }

  let game = null;
  function buildGame() {
    if (DATA.mode === 'puzzle') return runPuzzle();
    if (DATA.mode === 'shooter') return runShooter();
    return runRunner();
  }

  async function begin() {
    [playerImg, enemyImg, coinImg] = await Promise.all([
      loadImg(DATA.playerSprite), loadImg(DATA.enemySprite), loadImg(DATA.coinSprite),
    ]);
    startOverlay.classList.remove('show');
    gameOverOverlay.classList.remove('show');
    if (game) game.stop();
    game = buildGame();
    game.start();
    cv.focus();
  }

  document.getElementById('startBtn').addEventListener('click', begin);
  document.getElementById('restartBtn').addEventListener('click', begin);
</script>
</body>
</html>`;
}
